import { createContext, useCallback, useContext, useEffect, useState, useMemo } from "react";
import useUser, { getAuthorized, isUserDoctor, postAuthorized } from "../../../hooks/User";
import { useNavigate } from "react-router-dom";
import customParseFormat from 'dayjs/plugin/customParseFormat';
import dayjs from "dayjs";
import ProfileNavigation from "../../../components/common/ProfileNavigation";
import { ErrorHandlerContext, ErrorHandlerContextProvider } from "../../../contexts/ErrorHandlerContext";
import ErrorHandler from "../../../components/common/ErrorHandler";
import { ROUTES } from "../../../routes";
import { getPatientDisplayName, getPatientInitials, hasPatientName } from "../../../utils/patientUtils";
import {
    UserIcon,
    FileTextIcon,
    ClockIcon,
    CalendarIcon,
    RepeatIcon,
    BellIcon,
    MapPinIcon,
    PlusIcon,
    TrashIcon,
    XIcon,
    SearchIcon
} from "../../../components/icons";

import "./scheduler.css";
import SEO from "../../../components/SEO";

dayjs.extend(customParseFormat);

// Constants
const ScheduleTypeEnum = Object.freeze({
    OncePerDay: "once_per_day",
    MultipleTimesPerDay: "multiple_times_per_day",
    EveryFewDays: "every_few_days",
    Weekly: "weekly",
    Custom: "custom"
});

const daysOfWeek = ["monday", "tuesday", "wednesday", "thirsday", "friday", "saturday", "sunday"];

const russianDays = {
    monday: "Пн",
    tuesday: "Вт",
    wednesday: "Ср",
    thirsday: "Чт",
    friday: "Пт",
    saturday: "Сб",
    sunday: "Вс"
};

const russianDaysFull = {
    monday: "Понедельник",
    tuesday: "Вторник",
    wednesday: "Среда",
    thirsday: "Четверг",
    friday: "Пятница",
    saturday: "Суббота",
    sunday: "Воскресенье"
};

const TimezoneMap = Object.freeze({
    'Europe/Kaliningrad': 'Европа/Калининград',
    'Europe/Moscow': 'Европа/Москва',
    'Europe/Samara': 'Европа/Самара',
    'Asia/Yekaterinburg': 'Азия/Екатеринбург',
    'Asia/Omsk': 'Азия/Омск',
    'Asia/Krasnoyarsk': 'Азия/Красноярск',
    'Asia/Irkutsk': 'Азия/Иркутск',
    'Asia/Yakutsk': 'Азия/Якутск',
    'Asia/Vladivostok': 'Азия/Владивосток',
    'Asia/Magadan': 'Азия/Магадан',
    'Asia/Sakhalin': 'Азия/Сахалин',
    'Asia/Srednekolymsk': 'Азия/Среднеколымск',
    'Asia/Kamchatka': 'Азия/Камчатка',
    'Asia/Anadyr': 'Азия/Анадырь'
});

const ScheduleTypeLabels = {
    [ScheduleTypeEnum.OncePerDay]: "Один раз в день",
    [ScheduleTypeEnum.MultipleTimesPerDay]: "Несколько раз в день",
    [ScheduleTypeEnum.EveryFewDays]: "Каждые несколько дней",
    [ScheduleTypeEnum.Weekly]: "Еженедельно"
};

// Context
const CreateScheduleContext = createContext();

function CreateScheduleContextProvider({ children }) {
    const [title, setTitle] = useState("");
    const [descr, setDescr] = useState("");
    const [patient, setPatient] = useState(null);
    const [survey, setSurvey] = useState(null);
    const [times, setTimes] = useState(["12:00"]);
    const [daysCounter, setDaysCounter] = useState(2);
    const [weekDays, setWeekDays] = useState([]);
    const [beginDate, setBeginDate] = useState(dayjs().format("YYYY-MM-DD"));
    const [endDate, setEndDate] = useState(null);
    const [notifyCount, setNotifyCount] = useState(3);
    const [notifyDelay, setNotifyDelay] = useState(60);
    const [timezone, setTimezone] = useState(null);

    // Validation effects
    useEffect(() => { if (daysCounter < 2) setDaysCounter(2); }, [daysCounter]);
    useEffect(() => { if (notifyCount < 0) setNotifyCount(0); }, [notifyCount]);
    useEffect(() => { if (notifyDelay < 1) setNotifyDelay(1); }, [notifyDelay]);
    useEffect(() => { if (!times || times.length === 0) setTimes(["12:00"]); }, [times]);
    
    // Date validation
    useEffect(() => {
        const today = dayjs();
        const begin = dayjs(beginDate);
        if (today.isAfter(begin, "day")) {
            setBeginDate(today.format("YYYY-MM-DD"));
        }
    }, [beginDate]);

    useEffect(() => {
        if (!endDate) return;
        const begin = dayjs(beginDate);
        const end = dayjs(endDate);
        if (begin.isAfter(end)) {
            setEndDate(begin.format("YYYY-MM-DD"));
        }
    }, [beginDate, endDate]);

    const value = useMemo(() => ({
        title, setTitle, descr, setDescr,
        patient, setPatient, survey, setSurvey,
        times, setTimes, daysCounter, setDaysCounter,
        weekDays, setWeekDays, beginDate, setBeginDate,
        endDate, setEndDate, notifyCount, setNotifyCount,
        notifyDelay, setNotifyDelay, timezone, setTimezone
    }), [title, descr, patient, survey, times, daysCounter, weekDays, beginDate, endDate, notifyCount, notifyDelay, timezone]);

    return (
        <CreateScheduleContext.Provider value={value}>
            {children}
        </CreateScheduleContext.Provider>
    );
}

// Helper functions
function EvaluateEvery(days) {
    if (days % 10 === 1 && days > 20) return "каждый";
    return "каждые";
}

function EvaluateDays(days) {
    if (days < 5) return "дня";
    if (days <= 20) return "дней";
    if (days % 10 === 1) return "день";
    if (days % 10 < 5 && days % 10 !== 0) return "дня";
    return "дней";
}

function EvaluateCount(count) {
    if (count === 1) return "раз";
    if (count >= 5 && count <= 21) return "раз";
    if (count % 10 < 2 || count % 10 > 4) return "раз";
    return "раза";
}

function EvaluateEveryDelay(delay) {
    if (delay === 1 || (delay > 20 && delay % 10 === 1)) return "каждую";
    return "каждые";
}

function EvaluateDelayMinute(delay) {
    if (delay === 1 || (delay > 20 && delay % 10 === 1)) return "минуту";
    if (delay >= 5 && delay <= 20) return "минут";
    if (delay % 10 === 0 || delay % 10 > 4) return "минут";
    return "минуты";
}

function parseTime(timestring) {
    return dayjs(timestring, "HH:mm").format("HH:mm:ss.SSS[Z]");
}

// Patient Selection Modal
function PatientSelectionModal({ isOpen, onClose, onSelect }) {
    const [patients, setPatients] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!isOpen) return;
        setLoading(true);
        getAuthorized("/doctor/my_patients")
            .then((response) => setPatients(response.data?.patients || []))
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [isOpen]);

    const filteredPatients = useMemo(() => {
        if (!searchQuery.trim()) return patients;
        const query = searchQuery.toLowerCase();
        return patients.filter(p => 
            (p.full_name?.toLowerCase().includes(query)) ||
            (p.email?.toLowerCase().includes(query)) ||
            (p.username?.toLowerCase().includes(query))
        );
    }, [patients, searchQuery]);

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content modal-medium" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Выберите пациента</h2>
                    <button className="modal-close" onClick={onClose}><XIcon /></button>
                </div>
                <div className="modal-body">
                    <div className="search-box">
                        <SearchIcon />
                        <input
                            type="text"
                            placeholder="Поиск по имени или email..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    {loading ? (
                        <div className="loading-state">Загрузка...</div>
                    ) : filteredPatients.length === 0 ? (
                        <div className="empty-state">{searchQuery ? "Не найдено" : "Нет пациентов"}</div>
                    ) : (
                        <div className="selection-list">
                            {filteredPatients.map((patient) => (
                                <div
                                    key={patient.id}
                                    className="selection-item"
                                    onClick={() => { onSelect(patient); onClose(); }}
                                >
                                    <div className="selection-avatar">
                                        {getPatientInitials(patient)}
                                    </div>
                                    <div className="selection-info">
                                        <span className={`selection-name ${!hasPatientName(patient) ? 'no-name' : ''}`}>{getPatientDisplayName(patient)}</span>
                                        <span className="selection-email">{patient.email}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// Survey Selection Modal
function SurveySelectionModal({ isOpen, onClose, onSelect }) {
    const [surveys, setSurveys] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!isOpen) return;
        setLoading(true);
        getAuthorized("/doctor/my_surveys")
            .then((response) => setSurveys(response.data?.surveys || []))
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [isOpen]);

    const filteredSurveys = useMemo(() => {
        if (!searchQuery.trim()) return surveys;
        const query = searchQuery.toLowerCase();
        return surveys.filter(s => 
            (s.title?.toLowerCase().includes(query)) ||
            (s.description?.toLowerCase().includes(query))
        );
    }, [surveys, searchQuery]);

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content modal-medium" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Выберите опрос</h2>
                    <button className="modal-close" onClick={onClose}><XIcon /></button>
                </div>
                <div className="modal-body">
                    <div className="search-box">
                        <SearchIcon />
                        <input
                            type="text"
                            placeholder="Поиск по названию..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    {loading ? (
                        <div className="loading-state">Загрузка...</div>
                    ) : filteredSurveys.length === 0 ? (
                        <div className="empty-state">{searchQuery ? "Не найдено" : "Нет опросов"}</div>
                    ) : (
                        <div className="selection-list">
                            {filteredSurveys.map((survey) => (
                                <div
                                    key={survey.id}
                                    className="selection-item"
                                    onClick={() => { onSelect(survey); onClose(); }}
                                >
                                    <div className="selection-icon survey-icon"><FileTextIcon /></div>
                                    <div className="selection-info">
                                        <span className="selection-name">{survey.title}</span>
                                        {survey.description && (
                                            <span className="selection-desc">{survey.description}</span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// Form Components
function SectionCard({ icon: Icon, title, children, required = false }) {
    return (
        <div className="section-card">
            <div className="section-header">
                <div className="section-icon"><Icon /></div>
                <h3>{title}{required && <span className="required">*</span>}</h3>
            </div>
            <div className="section-content">
                {children}
            </div>
        </div>
    );
}

function SelectedPatientCard({ patient, onChange }) {
    if (!patient) {
        return (
            <button className="select-button" onClick={onChange}>
                <PlusIcon />
                <span>Выбрать пациента</span>
            </button>
        );
    }

    const displayName = getPatientDisplayName(patient);
    const initials = getPatientInitials(patient);
    const hasName = hasPatientName(patient);

    return (
        <div className="selected-card">
            <div className="selected-avatar">
                {initials}
            </div>
            <div className="selected-info">
                <span className={`selected-name ${!hasName ? 'no-name' : ''}`}>{displayName}</span>
                <span className="selected-email">{patient.email}</span>
            </div>
            <button className="btn-change" onClick={onChange}>Изменить</button>
        </div>
    );
}

function SelectedSurveyCard({ survey, onChange }) {
    if (!survey) {
        return (
            <button className="select-button" onClick={onChange}>
                <PlusIcon />
                <span>Выбрать опрос</span>
            </button>
        );
    }

    return (
        <div className="selected-card">
            <div className="selected-icon survey-icon"><FileTextIcon /></div>
            <div className="selected-info">
                <span className="selected-name">{survey.title}</span>
                {survey.description && <span className="selected-desc">{survey.description}</span>}
            </div>
            <button className="btn-change" onClick={onChange}>Изменить</button>
        </div>
    );
}

function TimeSelector({ times, setTimes, type }) {
    const isMultiple = type === ScheduleTypeEnum.MultipleTimesPerDay;

    const addTime = () => {
        if (times.length >= 10) return;
        setTimes([...times, times[times.length - 1] || "12:00"]);
    };

    const removeTime = (index) => {
        if (times.length <= 1 && !isMultiple) return;
        setTimes(times.filter((_, i) => i !== index));
    };

    const updateTime = (index, newTime) => {
        const newTimes = [...times];
        newTimes[index] = newTime;
        setTimes(newTimes);
    };

    return (
        <div className="time-selector">
            <div className="time-list">
                {times.map((time, index) => (
                    <div key={index} className="time-item">
                        <input
                            type="time"
                            value={time}
                            onChange={(e) => updateTime(index, e.target.value)}
                            className="time-input"
                        />
                        {(isMultiple || times.length > 1) && (
                            <button className="btn-remove-time" onClick={() => removeTime(index)} title="Удалить">
                                <TrashIcon />
                            </button>
                        )}
                    </div>
                ))}
            </div>
            {isMultiple && times.length < 10 && (
                <button className="btn-add-time" onClick={addTime}>
                    <PlusIcon />
                    <span>Добавить время</span>
                </button>
            )}
        </div>
    );
}

function WeekDaySelector({ weekDays, setWeekDays }) {
    const toggleDay = (day) => {
        if (weekDays.includes(day)) {
            setWeekDays(weekDays.filter(d => d !== day));
        } else {
            setWeekDays([...weekDays, day]);
        }
    };

    return (
        <div className="weekday-selector">
            {daysOfWeek.map((day) => (
                <button
                    key={day}
                    className={`weekday-btn ${weekDays.includes(day) ? 'active' : ''}`}
                    onClick={() => toggleDay(day)}
                    title={russianDaysFull[day]}
                >
                    {russianDays[day]}
                </button>
            ))}
        </div>
    );
}

function DayIntervalSelector({ days, setDays }) {
    return (
        <div className="day-interval">
            <span className="interval-text">{EvaluateEvery(days)}</span>
            <input
                type="number"
                min="2"
                value={days}
                onChange={(e) => setDays(Math.max(2, parseInt(e.target.value) || 2))}
                className="interval-input"
            />
            <span className="interval-text">{EvaluateDays(days)}</span>
        </div>
    );
}

function NotificationSettings({ count, setCount, delay, setDelay }) {
    return (
        <div className="notification-settings">
            <div className="notification-row">
                <span className="notification-label">Уведомить ещё</span>
                <input
                    type="number"
                    min="0"
                    max="10"
                    value={count}
                    onChange={(e) => setCount(Math.max(0, parseInt(e.target.value) || 0))}
                    className="notification-input"
                />
                <span className="notification-text">{EvaluateCount(count)}</span>
            </div>
            <div className="notification-row">
                <span className="notification-label">{EvaluateEveryDelay(delay)}</span>
                <input
                    type="number"
                    min="1"
                    max="1440"
                    value={delay}
                    onChange={(e) => setDelay(Math.max(1, parseInt(e.target.value) || 1))}
                    className="notification-input"
                />
                <span className="notification-text">{EvaluateDelayMinute(delay)}</span>
            </div>
        </div>
    );
}

// Main Scheduler Component
function Scheduler() {
    const navigate = useNavigate();
    const { setErrorList } = useContext(ErrorHandlerContext);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showPatientModal, setShowPatientModal] = useState(false);
    const [showSurveyModal, setShowSurveyModal] = useState(false);

    const {
        title, setTitle, descr, setDescr,
        patient, setPatient, survey, setSurvey,
        times, setTimes, daysCounter, setDaysCounter,
        weekDays, setWeekDays, beginDate, setBeginDate,
        endDate, setEndDate, notifyCount, setNotifyCount,
        notifyDelay, setNotifyDelay, timezone, setTimezone
    } = useContext(CreateScheduleContext);

    const [scheduleType, setScheduleType] = useState(ScheduleTypeEnum.OncePerDay);
    const [timezones, setTimezones] = useState([]);

    // Load timezones
    useEffect(() => {
        getAuthorized("/doctor/timezones", {})
            .then(response => {
                setTimezones(response.data?.timezones || []);
                if (!timezone) setTimezone('Asia/Yekaterinburg');
            })
            .catch(console.error);
    }, []);

    // Validation
    const validationErrors = useMemo(() => {
        const errors = [];
        if (!title.trim()) errors.push("Укажите название назначения");
        if (!patient) errors.push("Выберите пациента");
        if (!survey) errors.push("Выберите опрос");
        if (scheduleType === ScheduleTypeEnum.Weekly && weekDays.length === 0) {
            errors.push("Выберите хотя бы один день недели");
        }
        return errors;
    }, [title, patient, survey, scheduleType, weekDays]);

    const isValid = validationErrors.length === 0;

    const handleSubmit = useCallback(async () => {
        if (!isValid) {
            setErrorList(validationErrors);
            return;
        }

        setIsSubmitting(true);
        
        const body = {
            title: title.trim(),
            description: descr.trim() || undefined,
            survey_id: survey.id,
            patient_id: patient.id,
            frequency_type: scheduleType,
            scheduled_times: times.map(parseTime),
            start_date: beginDate,
            end_date: endDate,
            max_reminders: notifyCount,
            reminder_interval_minutes: notifyDelay,
            timezone: timezone
        };

        // Add type-specific fields
        switch(scheduleType) {
            case ScheduleTypeEnum.OncePerDay:
                body.scheduled_times = [parseTime(times[0])];
                break;
            case ScheduleTypeEnum.MultipleTimesPerDay:
                body.times_per_day = times.length;
                break;
            case ScheduleTypeEnum.EveryFewDays:
                body.interval_days = daysCounter;
                break;
            case ScheduleTypeEnum.Weekly:
                body.days_of_week = weekDays.map(day => daysOfWeek.indexOf(day));
                break;
        }

        try {
            await postAuthorized("/doctor/schedule_survey", body, {});
            navigate(ROUTES.PATIENTS, { viewTransition: true });
        } catch (error) {
            console.error("Failed to create schedule:", error);
            setErrorList([error.response?.data?.error_description || "Ошибка при создании назначения"]);
        } finally {
            setIsSubmitting(false);
        }
    }, [isValid, validationErrors, title, descr, survey, patient, scheduleType, times, beginDate, endDate, notifyCount, notifyDelay, timezone, daysCounter, weekDays, navigate, setErrorList]);

    return (
        <div className="scheduler-container">
            <div className="scheduler-header">
                <h1>Создание назначения</h1>
                <p className="scheduler-subtitle">Настройте расписание прохождения опроса для пациента</p>
            </div>

            <div className="scheduler-form">
                {/* Basic Info */}
                <SectionCard icon={FileTextIcon} title="Основная информация" required>
                    <div className="form-group">
                        <label>Название назначения *</label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Например: Утренний контроль давления"
                            className="form-input"
                        />
                    </div>
                    <div className="form-group">
                        <label>Описание</label>
                        <textarea
                            value={descr}
                            onChange={(e) => setDescr(e.target.value)}
                            placeholder="Дополнительная информация для пациента..."
                            className="form-textarea"
                            rows={3}
                        />
                    </div>
                </SectionCard>

                {/* Selection */}
                <SectionCard icon={UserIcon} title="Пациент и опрос" required>
                    <div className="form-group">
                        <label>Пациент *</label>
                        <SelectedPatientCard patient={patient} onChange={() => setShowPatientModal(true)} />
                    </div>
                    <div className="form-group">
                        <label>Опрос *</label>
                        <SelectedSurveyCard survey={survey} onChange={() => setShowSurveyModal(true)} />
                    </div>
                </SectionCard>

                {/* Schedule Type */}
                <SectionCard icon={RepeatIcon} title="Тип расписания">
                    <div className="schedule-type-selector">
                        {Object.entries(ScheduleTypeLabels).map(([value, label]) => (
                            <button
                                key={value}
                                className={`type-btn ${scheduleType === value ? 'active' : ''}`}
                                onClick={() => setScheduleType(value)}
                            >
                                {label}
                            </button>
                        ))}
                    </div>

                    {/* Type-specific options */}
                    {scheduleType === ScheduleTypeEnum.Weekly && (
                        <div className="type-specific">
                            <label>Дни недели *</label>
                            <WeekDaySelector weekDays={weekDays} setWeekDays={setWeekDays} />
                        </div>
                    )}

                    {scheduleType === ScheduleTypeEnum.EveryFewDays && (
                        <div className="type-specific">
                            <label>Интервал</label>
                            <DayIntervalSelector days={daysCounter} setDays={setDaysCounter} />
                        </div>
                    )}
                </SectionCard>

                {/* Time */}
                <SectionCard icon={ClockIcon} title="Время">
                    <TimeSelector times={times} setTimes={setTimes} type={scheduleType} />
                </SectionCard>

                {/* Date Range */}
                <SectionCard icon={CalendarIcon} title="Период действия">
                    <div className="date-range">
                        <div className="date-group">
                            <label>Начало *</label>
                            <input
                                type="date"
                                value={beginDate}
                                onChange={(e) => setBeginDate(e.target.value)}
                                className="form-input"
                                min={dayjs().format("YYYY-MM-DD")}
                            />
                        </div>
                        <div className="date-group">
                            <label>Окончание</label>
                            <input
                                type="date"
                                value={endDate || ""}
                                onChange={(e) => setEndDate(e.target.value || null)}
                                className="form-input"
                                min={beginDate}
                            />
                            {endDate && (
                                <button className="btn-clear-date" onClick={() => setEndDate(null)}>
                                    Очистить
                                </button>
                            )}
                        </div>
                    </div>
                </SectionCard>

                {/* Timezone */}
                <SectionCard icon={MapPinIcon} title="Часовой пояс">
                    <select
                        value={timezone || "Asia/Yekaterinburg"}
                        onChange={(e) => setTimezone(e.target.value)}
                        className="form-select"
                    >
                        {timezones.map((tz) => (
                            <option key={tz.name} value={tz.name}>
                                {TimezoneMap[tz.name] || tz.name} ({tz.utc_offset})
                            </option>
                        ))}
                    </select>
                </SectionCard>

                {/* Notifications */}
                <SectionCard icon={BellIcon} title="Напоминания">
                    <NotificationSettings
                        count={notifyCount}
                        setCount={setNotifyCount}
                        delay={notifyDelay}
                        setDelay={setNotifyDelay}
                    />
                </SectionCard>

                {/* Submit */}
                <div className="scheduler-actions">
                    <button
                        className="btn-primary btn-submit"
                        onClick={handleSubmit}
                        disabled={!isValid || isSubmitting}
                    >
                        {isSubmitting ? "Создание..." : "Создать назначение"}
                    </button>
                    {!isValid && validationErrors.length > 0 && (
                        <div className="validation-hint">
                            Заполните обязательные поля: {validationErrors.join(", ")}
                        </div>
                    )}
                </div>
            </div>

            {/* Modals */}
            <PatientSelectionModal
                isOpen={showPatientModal}
                onClose={() => setShowPatientModal(false)}
                onSelect={setPatient}
            />
            <SurveySelectionModal
                isOpen={showSurveyModal}
                onClose={() => setShowSurveyModal(false)}
                onSelect={setSurvey}
            />
        </div>
    );
}

// Main Page
export default function SchedulerPage() {
    const { data, loading, error } = useUser();
    const navigate = useNavigate();

    useEffect(() => {
        if (loading) return;
        if (error) navigate(ROUTES.AUTH, { viewTransition: true });
        if (!isUserDoctor(data)) navigate(ROUTES.PROFILE, { viewTransition: true });
    }, [data, loading, error, navigate]);

    if (loading) {
        return (
            <>
                <ProfileNavigation isDoctor={true} />
                <div className="scheduler-loading">
                    <div className="spinner"></div>
                    <p>Загрузка...</p>
                </div>
            </>
        );
    }

    return (
        <>
            <SEO 
                title="Планировщик опросов" 
                description="Создание и управление расписанием медицинских опросов для пациентов" 
                noindex={true} 
            />
            <CreateScheduleContextProvider>
                <ErrorHandlerContextProvider>
                    <ProfileNavigation isDoctor={isUserDoctor(data)} />
                    <div className="scheduler-page">
                        <Scheduler />
                    </div>
                    <ErrorHandler />
                </ErrorHandlerContextProvider>
            </CreateScheduleContextProvider>
        </>
    );
}

export { EvaluateEvery, EvaluateDays, TimezoneMap };
